import AzureIcon from 'components/icons/AzureIcon';
import ZoomIcon from 'components/icons/ZoomIcon';
import TeamsIcon from 'components/icons/TeamsIcon';
import WebexIcon from 'components/icons/WebexIcon';
import SBCIcon from 'components/icons/SBCIcon';
import EightByEightIcon from 'components/icons/EightByEightIcon';
import RingCentralIcon from 'components/icons/RingCentralIcon';
import DialPadIcon from 'components/icons/DialPadIcon';
import GCCHighIcon from 'components/icons/GCCHighIcon';
import OperatorPlatformIcon from 'components/icons/OperatorPlatformIcon';

import AccordionItemPillHandler from 'components/forms/new/AccordionItemPillHandler';
import StandardSection from './StandardSection';
import EightByEightSection from './EightByEightSection';
import MicrosoftTeamsSection from './MicrosoftTeamsSection';
import StatusCellRenderer from '../cellRenderer/StatusCellRenderer';
import Text from '@/components/v4/Text';
import { PlatformEnums } from '@/utils/platforms';
import { HStack, Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useDashboardServices } from 'hooks/useCustomQuery';
import OperatorPlatformSection from './OperatorPlatformSection';
import { CLOUD_SIP_LABEL } from '@/constants/sbcaas';

const PlatformAccordion = ({
    platformId,
    platform,
    errors,
    touched,
    value,
    submitData,
    setSubmitData,
    teamsTrunks,
    callingProfiles,
    OC,
    dragHandleProps,
    platformIndex,
    platformNumbersLength,
    selectedTrunk,
    setSelectedTrunk,
    previousData,
    isInGroup = false,
    onRemovePlatform,
    onMoveUp,
    onMoveDown,
    isFirst = false,
    isLast = false,
}) => {
    const previousPlatform =
        previousData?.find((item) => item.platformID === platformId) || {};

    const platformOptions = submitData?.find(
        (item) => item.platformID === platformId,
    )?.availableOptions;

    const { currentCompany } = useSelector((state) => state.navigationLists);

    const { data: services } = useDashboardServices({ currentCompany });

    const platforms = {
        [PlatformEnums.AZURE]: {
            name: 'Azure Communication Services',
            key: 'azure',
            icon: AzureIcon,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    section: 'azure',
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
            disabled: true,
        },
        [PlatformEnums.WEBEX]: {
            name: 'Cisco Webex',
            key: 'webex',
            icon: WebexIcon,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    section: 'webex',
                    isLocationDisabled: false,
                    platformLocations: platformOptions?.addresses,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
            disabled: true,
        },
        [PlatformEnums.ZOOM]: {
            name: 'Zoom',
            key: 'zoom',
            icon: ZoomIcon,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    section: 'zoom',
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
            disabled: false,
        },
        [PlatformEnums.SBC]: {
            name: CLOUD_SIP_LABEL,
            key: 'SBCaaS',
            icon: SBCIcon,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    section: 'SBCaaS',
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
            disabled: true,
        },
        [PlatformEnums.TEAMS]: {
            name: 'Microsoft Teams',
            key: 'teams',
            icon: TeamsIcon,
            disabled: false,
            fields: (props) =>
                MicrosoftTeamsSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    teamsTrunks: teamsTrunks,
                    callingProfiles: callingProfiles,
                    OC: OC,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    platform: platform,
                    services: services,
                    ...props,
                }),
        },
        [PlatformEnums.EIGHTBYEIGHT]: {
            name: '8x8',
            key: '8x8',
            icon: EightByEightIcon,
            disabled: false,
            fields: (props) =>
                EightByEightSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
        },
        [PlatformEnums.RINGCENTRAL]: {
            name: 'Ring Central',
            key: 'ringcentral',
            icon: RingCentralIcon,
            disabled: false,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
        },
        [PlatformEnums.DIALPAD]: {
            name: 'Dial Pad',
            key: 'dialpad',
            icon: DialPadIcon,
            disabled: false,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    ...props,
                }),
        },
        [PlatformEnums.OPERATOR_PLATFORM]: {
            name: 'Operator Platform',
            key: 'operatorPlatform',
            icon: OperatorPlatformIcon,
            disabled: false,
            fields: (props) =>
                OperatorPlatformSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    services: platformOptions?.trunks,
                    ...props,
                }),
        },
        [PlatformEnums.GCC]: {
            name: 'GCC',
            key: 'gcc',
            icon: GCCHighIcon,
            disabled: false,
            fields: (props) =>
                StandardSection({
                    errors: errors,
                    touched: touched,
                    submitData: submitData,
                    setSubmitData: setSubmitData,
                    platformTrunks: platformOptions?.trunks,
                    platformID: platformId,
                    selectedTrunk: selectedTrunk,
                    setSelectedTrunk: setSelectedTrunk,
                    section: 'gcc',
                    ...props,
                }),
        },
    };

    const currentPlatform = platforms[platformId];

    const status =
        previousPlatform?.platformStatus === 0 &&
        previousPlatform?.trunkID == null
            ? 'disabled'
            : previousPlatform?.platformStatus;

    const accordionAdornment = (
        <HStack paddingRight={10}>
            {platform?.user && (
                <Text minWidth="200px" fontWeight="bold">
                    {platform?.user}
                </Text>
            )}
            <Box width="110px">
                <StatusCellRenderer value={status} />
            </Box>
        </HStack>
    );

    // Calculate order number (1-indexed) for standalone platforms
    const orderNumber = !isInGroup ? platformIndex + 1 : null;

    return (
        <AccordionItemPillHandler
            index={platformId}
            key={currentPlatform?.key}
            icon={currentPlatform?.icon}
            label={currentPlatform?.name}
            errors={errors}
            touched={touched}
            values={value}
            // isDisabled={currentPlatform?.disabled}
            accordionAdornment={accordionAdornment}
            dragHandleProps={isInGroup ? null : dragHandleProps}
            platformIndex={platformIndex}
            noOrder={platformNumbersLength === 1 || isInGroup}
            platformsLength={platformNumbersLength}
            orderNumber={orderNumber}
            isInGroup={isInGroup}
            onRemovePlatform={onRemovePlatform}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            isFirst={isFirst}
            isLast={isLast}>
            {currentPlatform?.fields?.({
                selectedTrunk,
                setSelectedTrunk,
            })}
        </AccordionItemPillHandler>
    );
};

export default PlatformAccordion;
